import { useCallback, useState } from "react";

import { SubscriptionFrequency, IPatient, PatientStatus, GroupedItemFlavors } from "common/types";
import { CartCreatorKind, ClientCartDetail, ClientCartItem } from "common/types/carts";
import { parseNumber } from "common/utils/numbers";
import { toDate, toLocaleDate } from "common/utils/dates";
import { DeliveryMethodKind, UnavailableReason } from "common/types/fulfillments";
import { deepCombineErrors } from "common/utils/errors";
import { pluralized } from "common/utils/strings";
import { indexByPrimaryKey } from "common/utils/entities";
import { getStateName } from "common/constants/address";

import { CheckoutStepProps } from "client/checkout/types";
import CartItemSummary from "client/carts/components/shared/CartItemSummary";
import PatientSelect from "client/accounts/components/PatientSelect";
import PatientPickerChrome from "client/accounts/components/PatientPickerChrome";
import { Option as PatientOption, isPatient } from "client/accounts/components/PatientSelectPanel";
import PetForm from "client/accounts/components/forms/PetForm";
import {
  useCreatePatientMutation,
  usePaginatedPatientsQuery,
  usePatientPickerList,
} from "client/accounts/hooks";
import { PATIENT_LIST_PAGE_SIZE } from "client/accounts/hooks/constants";
import {
  useCartItemAssignmentMutation,
  useCartItemSubscriptionCreateUpdateMutation,
  useCartItemSubscriptionDeleteMutation,
  useCartItemUpdateMutationV2,
  useUpdateCartDeliveryMutation,
} from "client/carts/hooks";
import Modal from "client/common/components/deprecated-modals/Modal";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import QuantityInput from "client/common/components/inputs/QuantityInput";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import TextChip from "client/common/components/content/TextChip";
import ProductLineSelections from "client/products/components/ProductLineSelections";
import Banner from "client/common/components/content/Banner";
import Alert from "client/common/components/content/Alert";
import { CartItemRefillTooSoonSummary } from "client/carts/types";
import {
  getCartItemPricing,
  sendAddToCart,
  sendRemoveFromCart,
  getAddToCartAutoshipFields,
  useSendPostHogAddToCart,
  useSendPostHogRemoveFromCart,
} from "client/analytics/utils";
import { AddToCartPageSource, FreeShippingProgressSurface } from "client/analytics/types";
import { useBreakpoints } from "client/common/hooks";
import { useFeatures } from "client/clinics/hooks";
import PromoCodes from "client/carts/components/PromoCodes";
import FreeShippingProgress from "client/carts/components/shared/FreeShippingProgress";
import { useRefillTooSoonDrawer, useRemoveCartItemDialog } from "client/checkout/stackables";
import { useInvalidateAllCartQueries } from "client/carts/hooks/invalidate";
import Select from "client/common/components/inputs/Select";
import { useAccountUpdatePetDrawer, useAccountUpdateDrawer } from "client/accounts/stackables";
import { refillTooSoonOptions } from "client/checkout/constants";

import StepLayout from "../CheckoutStepLayout";
import CheckoutSummary from "../CheckoutSummary";
import { isUnfillableAtDestination } from "../../utils";

import styles from "../../styles/ItemOptionsCheckoutStep.module.less";

type AssignmentRow = {
  patient: IPatient;
  quantity: number;
};

type CartItemAssignmentModalProps = {
  cartItem: ClientCartItem;
  cart: ClientCartDetail;
  isVisible: boolean;
  onClose: () => void;
};

const CartItemAssignmentModal = ({
  isVisible,
  cart,
  cartItem,
  onClose,
}: CartItemAssignmentModalProps) => {
  const [assignments, setAssignments] = useState<Record<number, AssignmentRow>>({});
  const {
    qInput,
    onQChange,
    setPage,
    results,
    paginator,
    showChrome,
    isLoading: isPatientsLoading,
    error: patientsError,
  } = usePatientPickerList({
    enabled: isVisible,
  });

  const totalAssigned = Object.values(assignments).reduce(
    (sum, assignment) => sum + assignment.quantity,
    0,
  );

  const handleClose = () => {
    setAssignments({});
    onClose();
  };

  const {
    mutate: assignCartItems,
    isPending: isSubmitting,
    error,
  } = useCartItemAssignmentMutation(cart.id, {
    onSuccess: handleClose,
  });

  const onSubmit = () => {
    const body = {
      cart_item_id: cartItem.id,
      assignments: Object.values(assignments)
        .filter((assignment) => assignment.quantity > 0)
        .map((assignment) => ({
          patient_id: assignment.patient.id,
          quantity: assignment.quantity,
        })),
    };
    assignCartItems(body);
  };

  const resultIds = new Set(results.map((patient) => patient.id));
  const pinnedPatients = Object.values(assignments)
    .filter((assignment) => assignment.quantity > 0 && !resultIds.has(assignment.patient.id))
    .map((assignment) => assignment.patient);
  const rows = [...pinnedPatients, ...results];

  const setQuantity = (patient: IPatient, quantity: number) => {
    setAssignments((current) => {
      if (quantity <= 0) {
        const next = { ...current };
        delete next[patient.id];
        return next;
      }
      return { ...current, [patient.id]: { patient, quantity } };
    });
  };

  return (
    <Modal visible={isVisible} onClose={handleClose}>
      <ModalLayout>
        <ModalLayout.Header title="Select dose per pet" onClose={handleClose} />
        <ModalLayout.Form
          submitText="Update dose"
          canSubmit={totalAssigned === cartItem?.quantity}
          onSubmit={onSubmit}
          isLoading={isSubmitting}
          error={error}
        >
          <PatientPickerChrome
            q={qInput}
            onQChange={onQChange}
            paginator={paginator}
            onPageChange={setPage}
            showChrome={showChrome}
          >
            {isPatientsLoading && <LoadingContainer />}
            {!!patientsError && results.length === 0 && <FetchErrorAlert error={patientsError} />}
            {!isPatientsLoading &&
              !(patientsError && results.length === 0) &&
              rows.map((patient) => (
                <ModalLayout.Form.Group key={patient.id}>
                  <div className={styles.dose}>
                    <div>{patient.name_display}</div>
                    <QuantityInput
                      onChange={(val) => setQuantity(patient, val)}
                      value={assignments[patient.id]?.quantity ?? 0}
                      min={0}
                      max={
                        cartItem.quantity - totalAssigned + (assignments[patient.id]?.quantity ?? 0)
                      }
                    />
                  </div>
                </ModalLayout.Form.Group>
              ))}
          </PatientPickerChrome>
        </ModalLayout.Form>
      </ModalLayout>
    </Modal>
  );
};

type AddPatientModalProps = {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: (patient: IPatient) => void;
};

const AddPatientModal = ({ isVisible, onClose, onSuccess }: AddPatientModalProps) => {
  const {
    mutate: createPatient,
    isPending: isLoading,
    error,
  } = useCreatePatientMutation({
    onSuccess: (patient) => {
      onClose();
      onSuccess(patient);
    },
  });

  return (
    <Modal visible={isVisible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header title="Add pet" onClose={onClose} />
        <PetForm
          isLoading={isLoading}
          submitText="Add Pet"
          error={error}
          onSubmit={(values) =>
            createPatient({
              ...values,
              weight_in_units: parseNumber(values.weight_in_units, 0) || null,
              weight_unit_of_measure: values.weight_unit_of_measure,
              birth_day: parseNumber(values.birth_day, 0) || null,
              birth_month: parseNumber(values.birth_month, 0) || null,
              birth_year: parseNumber(values.birth_year, 0) || null,
              primary_breed: null,
              secondary_breeds: [],
              color: null,
              custom_breed: "",
              custom_species: "",
              status: PatientStatus.Active,
              image_id: values.image?.id ?? null,
            })
          }
        />
      </ModalLayout>
    </Modal>
  );
};

type CartItemSelectionsProps = {
  cartItem: ClientCartItem;
  refillTooSoonSummary: CartItemRefillTooSoonSummary | undefined;
  cart: ClientCartDetail;
  heading: string;
};

const CartItemSelections = ({
  cartItem,
  refillTooSoonSummary,
  cart,
  heading,
}: CartItemSelectionsProps) => {
  const [isAddPatientModalVisible, setIsAddPatientModalVisible] = useState(false);
  const [isSplitPatientModalVisible, setIsSplitPatientModalVisible] = useState(false);

  const { open: openRemoveCartItemDialog } = useRemoveCartItemDialog();
  const { open: openRefillTooSoonDrawer } = useRefillTooSoonDrawer();

  const itemErrorActions = [
    {
      label: "Remove item",
      onClick: () => openRemoveCartItemDialog({ cartId: cart.id, cartItem }),
    },
  ];

  const { isPhoneAndSmaller } = useBreakpoints();

  const isEdbEnabled = useFeatures().AUTOSHIP_EDB;

  const { mutate: updateCartDelivery, error: updateCartDeliveryError } =
    useUpdateCartDeliveryMutation(cart.id);

  const { mutate: updateCartItemPatient, error: updateCartItemPatientError } =
    useCartItemUpdateMutationV2(cartItem.id, cart.id);

  const { mutate: updateCartItemSelections, error: updateCartItemSelectionsError } =
    useCartItemUpdateMutationV2(cartItem.id, cart.id);

  const { mutate: updateCartItemQuantity, error: updateCartItemQuantityError } =
    useCartItemUpdateMutationV2(cartItem.id, cart.id);
  const sendPostHogAddToCart = useSendPostHogAddToCart();
  const sendPostHogRemoveFromCart = useSendPostHogRemoveFromCart();

  const { mutate: createUpdateSubscription, error: createUpdateSubscriptionError } =
    useCartItemSubscriptionCreateUpdateMutation(cart.id, cartItem.id);

  const { mutate: deleteSubscription, error: deleteSubscriptionError } =
    useCartItemSubscriptionDeleteMutation(cart.id, cartItem.id);

  const invalidateCartQueries = useInvalidateAllCartQueries();

  const subscriptionError = createUpdateSubscriptionError || deleteSubscriptionError;

  const selectPatient = useCallback(
    (patient: IPatient) => {
      updateCartItemPatient({
        body: { patient: patient.id },
        optimisticData: { patient },
      });
    },
    [updateCartItemPatient],
  );

  const selectPickup = (isPickup: boolean) => {
    const updates = [
      {
        cart_item_id: cartItem.id,
        is_pickup: isPickup,
      },
    ];
    const newCartItems = cart.items.map((ci) =>
      ci.id === cartItem.id ? { ...ci, is_pickup: isPickup } : ci,
    );

    updateCartDelivery({ body: updates, optimisticData: { newCartItems } });
  };

  const renderFulfillmentSource = () => {
    if (cartItem.is_available) {
      return (
        <ProductLineSelections.DeliveryMethodSelectPanel
          value={cartItem.is_pickup ? DeliveryMethodKind.Pickup : DeliveryMethodKind.Shipping}
          onChange={(val) => selectPickup(val === DeliveryMethodKind.Pickup)}
          fulfillmentOptions={cartItem.fulfillment_options}
        />
      );
    }
    if (isUnfillableAtDestination(cartItem)) {
      // The shipping address decides this and, when the cart needs one, is chosen on the next
      // step, which step validation lets the client reach, so removal is not the only way out
      // and this must not scroll.
      const shippingState = cart.shipping_address?.state;
      const destinationName = shippingState ? getStateName(shippingState) : "your shipping address";
      const productLineName = cartItem.item.product.product_line.name;
      if (cartItem.unavailable_reason === UnavailableReason.ControlledAutoshipBlocked) {
        return (
          <Alert
            variant="warning"
            actions={[{ label: "Make it a one-time order", onClick: () => deleteSubscription() }]}
          >
            {productLineName} can&apos;t be sent on Autoship to {destinationName}. Make it a
            one-time order, or continue to choose a different address.
          </Alert>
        );
      }
      const textByReason: Record<
        Exclude<UnavailableReason, typeof UnavailableReason.ControlledAutoshipBlocked>,
        string
      > = {
        [UnavailableReason.ControlledDispensingBlocked]: cart.is_shipping_address_required
          ? `${productLineName} can't be shipped to ${destinationName}.`
          : `${productLineName} can't be dispensed at your clinic.`,
        [UnavailableReason.ControlledStateUnfillable]:
          "None of the pharmacies that can ship this item to your shipping address have it in stock.",
      };
      return (
        <Alert variant="warning">
          {textByReason[cartItem.unavailable_reason]}{" "}
          {cart.is_shipping_address_required
            ? "Continue to choose a different address, or remove it."
            : "Remove it to continue."}
        </Alert>
      );
    }
    return (
      <Alert variant="danger" shouldScrollOnMount={true} actions={itemErrorActions}>
        This item is currently unavailable.
      </Alert>
    );
  };

  const selectSubscription = (frequency: SubscriptionFrequency | null) => {
    if (!frequency) {
      deleteSubscription();
    } else {
      createUpdateSubscription(frequency);
    }
  };

  const renderPrescriptionTag = (patientOption: PatientOption) => {
    if (!isPatient(patientOption)) {
      return null;
    }
    const activePrescription = cartItem.available_prescriptions.find(
      (prescription) => prescription.patient.id === patientOption.id,
    );

    if (!activePrescription) {
      return null;
    }

    return (
      <TextChip size="s" variant="primary">
        Rx on file
      </TextChip>
    );
  };

  const onPetUpdateSuccess = () => {
    invalidateCartQueries();
  };

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    weekday: "long",
    month: "long",
    day: "numeric",
  };

  // we handle clinic-created carts slightly differently under the assumption that
  // clients will enter the checkout flow from the checkout magic link (and not see a cart)
  const isClinicCreatedCart = cart.creator_type === CartCreatorKind.Clinic;

  const flavorSelection = cartItem.flavor_selection ?? null;
  const flavorSelectionOptionsGroup: GroupedItemFlavors | null = cartItem.item.flavor_options
    ?.length
    ? cartItem.item.flavor_options[0]
    : null;

  const onSelectionChange = (val: string) => {
    const newSelectionId = parseNumber(val, 0);
    if (flavorSelection && newSelectionId === flavorSelection) {
      return;
    }
    const newFlavorSelection = flavorSelectionOptionsGroup?.options.find(
      (s) => s.id === newSelectionId,
    );
    if (!newFlavorSelection) {
      return;
    }
    updateCartItemSelections({
      body: { flavor_selection: newFlavorSelection?.id },
      optimisticData: {
        flavor_selection: newFlavorSelection,
      },
    });
  };

  const onUpdateQuantity = (quantity: number) => {
    const { price, discount } = getCartItemPricing(cartItem);
    if (quantity > cartItem.quantity) {
      const quantityAdded = quantity - cartItem.quantity;
      sendAddToCart({
        item: cartItem.item,
        quantity: quantityAdded,
        price,
        discount,
      });
      sendPostHogAddToCart({
        source: AddToCartPageSource.Checkout,
        cart_id: cart.id,
        cart_item_id: cartItem.id,
        item_id: cartItem.item.id,
        product_id: cartItem.item.product.id,
        product_line_id: cartItem.item.product.product_line.id,
        patient_id: cartItem.patient?.id ?? null,
        flavor_selection_id: cartItem.flavor_selection ?? null,
        quantity: quantityAdded,
        unit_price: price,
        discount,
        ...getAddToCartAutoshipFields(cartItem.subscription),
      });
    }
    if (quantity < cartItem.quantity) {
      const quantityRemoved = cartItem.quantity - quantity;
      sendRemoveFromCart({
        item: cartItem.item,
        quantity: quantityRemoved,
        price,
        discount,
      });
      sendPostHogRemoveFromCart({
        cart_id: cart.id,
        cart_item_id: cartItem.id,
        item_id: cartItem.item.id,
        patient_id: cartItem.patient?.id ?? null,
        flavor_selection_id: cartItem.flavor_selection ?? null,
        quantity: quantityRemoved,
        unit_price: price,
        discount,
      });
    }
    updateCartItemQuantity({ body: { quantity } });
  };

  const { open: openAccountPetUpdate } = useAccountUpdatePetDrawer();
  const petId = cartItem.patient?.id ?? 0;

  const steps = [
    {
      visible: true,
      title: `Select ${pluralized(cartItem.quantity, "Pet")} for this medication`,
      content: (
        <>
          {updateCartItemPatientError && (
            <StepLayout.FetchErrorAlert
              error={updateCartItemPatientError}
              fieldErrors={[deepCombineErrors(updateCartItemPatientError.errors)]}
              actions={itemErrorActions}
            />
          )}
          {!!cartItem.missing_required_patient_fields_message && (
            <Alert
              variant="danger"
              shouldScrollOnMount={true}
              actions={[
                {
                  label: "Update pet",
                  onClick: () =>
                    openAccountPetUpdate({
                      id: petId,
                      onSuccess: onPetUpdateSuccess,
                    }),
                  hasRightIcon: true,
                },
              ]}
            >
              {cartItem.missing_required_patient_fields_message}
            </Alert>
          )}
          {!!cartItem.invalid_patient_species_message && (
            <Alert variant="warning" shouldScrollOnMount={true} actions={undefined}>
              {cartItem.invalid_patient_species_message}
            </Alert>
          )}
          {!!cartItem.invalid_patient_weight_message && (
            <Alert variant="warning" shouldScrollOnMount={true} actions={undefined}>
              {cartItem.invalid_patient_weight_message}
            </Alert>
          )}
          <StepLayout.Section>
            <PatientSelect
              value={cartItem.patient}
              onSelect={selectPatient}
              recentPatients={cart.items.flatMap((item) => (item.patient ? [item.patient] : []))}
              canSplit={cartItem.quantity > 1}
              onSelectMulti={() => setIsSplitPatientModalVisible(true)}
              onAdd={() => setIsAddPatientModalVisible(true)}
              renderTag={renderPrescriptionTag}
            />
          </StepLayout.Section>
        </>
      ),
    },
    ...(flavorSelectionOptionsGroup
      ? [
          {
            // allow selections in clinic-created cart checkouts because the client
            // did not have a change to choose on PDP
            visible: isClinicCreatedCart,
            title: `Choose your ${flavorSelectionOptionsGroup.label.toLowerCase()}`,
            content: (
              <Select
                label={flavorSelectionOptionsGroup.label}
                value={flavorSelection ? `${flavorSelection}` : ""}
                onChange={onSelectionChange}
              >
                {flavorSelectionOptionsGroup.options.map((option) => (
                  <Select.Option key={option.id} value={`${option.id}`}>
                    {option.display}
                  </Select.Option>
                ))}
              </Select>
            ),
          },
        ]
      : []),
    {
      visible: !cartItem.is_available || cartItem.fulfillment_options.length > 1,
      title: "Choose my fulfillment source",
      content: (
        <>
          {updateCartDeliveryError && (
            <StepLayout.FetchErrorAlert
              error={updateCartDeliveryError}
              actions={itemErrorActions}
            />
          )}
          <StepLayout.Section>{renderFulfillmentSource()}</StepLayout.Section>
        </>
      ),
    },
    {
      visible: cartItem.is_subscribable,
      title: "Subscribe and Save",
      content: (
        <>
          {subscriptionError && (
            <StepLayout.FetchErrorAlert
              error={subscriptionError}
              fieldErrors={[deepCombineErrors(subscriptionError.errors)]}
              actions={itemErrorActions}
            />
          )}
          <StepLayout.Section>
            <ProductLineSelections.SubscriptionSelectPanel
              item={{
                ...cartItem,
                recommended_frequency: cartItem.recommended_frequency,
                eligible_for_autoship_discounts: cartItem.item.eligible_for_autoship_discounts,
              }}
              onChange={selectSubscription}
              value={
                cartItem.subscription
                  ? {
                      ...cartItem.subscription,
                      display: cartItem.subscription.frequency_display,
                    }
                  : null
              }
              quantity={cartItem.quantity}
            />
            {cartItem.existing_subscription_modifications && (
              <Banner>
                <Banner.Content
                  label="Your existing Autoship for this item will be updated to match these new preferences:"
                  description={
                    <ul>
                      {!isEdbEnabled &&
                        cartItem.existing_subscription_modifications.next_order_date && (
                          <li>
                            Next order date:{" "}
                            {toDate(cartItem.existing_subscription_modifications.next_order_date)}
                          </li>
                        )}
                      {isEdbEnabled &&
                        cartItem.existing_subscription_modifications.expected_delivery_by && (
                          <li>
                            Next estimated delivery:{" "}
                            {toDate(
                              cartItem.existing_subscription_modifications.expected_delivery_by,
                            )}
                          </li>
                        )}
                      {cartItem.existing_subscription_modifications.frequency && (
                        <li>Frequency: {cartItem.existing_subscription_modifications.frequency}</li>
                      )}
                      {cartItem.existing_subscription_modifications.quantity && (
                        <li>Quantity: {cartItem.existing_subscription_modifications.quantity}</li>
                      )}
                      {cartItem.existing_subscription_modifications.payment_method && (
                        <li>
                          Payment: {cartItem.existing_subscription_modifications.payment_method}
                        </li>
                      )}
                    </ul>
                  }
                />
              </Banner>
            )}
          </StepLayout.Section>
        </>
      ),
    },
  ].filter((step) => step.visible);

  return (
    <StepLayout.Item>
      <StepLayout.Header>
        <StepLayout.Heading>{heading}</StepLayout.Heading>
      </StepLayout.Header>
      {updateCartItemQuantityError && (
        <StepLayout.FetchErrorAlert
          error={updateCartItemQuantityError}
          fieldErrors={[deepCombineErrors(updateCartItemQuantityError.errors)]}
          actions={itemErrorActions}
        />
      )}
      {updateCartItemSelectionsError && (
        <StepLayout.FetchErrorAlert
          error={updateCartItemSelectionsError}
          fieldErrors={[deepCombineErrors(updateCartItemSelectionsError.errors)]}
          actions={itemErrorActions}
        />
      )}
      <StepLayout.Item.Info>
        <div className={styles.cartItemSummaryContainer}>
          <CartItemSummary
            cartItem={cartItem}
            hasPatient={false}
            hasSelections={!isClinicCreatedCart}
            hasFrequency={false}
            canUpdateQuantity={true}
            onUpdateQuantity={onUpdateQuantity}
            canRemoveItem={true}
            hasPrice={isPhoneAndSmaller}
            onRemoveItem={() => openRemoveCartItemDialog({ cartId: cart.id, cartItem })}
          />
          {!cartItem.refill_too_soon_reason &&
            refillTooSoonSummary &&
            !refillTooSoonSummary.is_valid_by_refill_too_soon && (
              <Alert
                variant="danger"
                actions={[
                  {
                    label: "I have a special circumstance",
                    onClick: () => openRefillTooSoonDrawer({ cartId: cart.id, cartItem }),
                    hasRightIcon: true,
                  },
                ]}
              >
                <div className={styles.alertRTS}>
                  It&apos;s too soon to order a refill! Please remove item until it is available for
                  order
                  {refillTooSoonSummary.earliest_date_to_refill
                    ? ` beginning on ${toLocaleDate(
                        refillTooSoonSummary.earliest_date_to_refill,
                        dateFormatOptions,
                      )}`
                    : ""}
                  .
                </div>
              </Alert>
            )}
          {!!cartItem.refill_too_soon_reason && (
            <Alert
              actions={[
                {
                  label: "Edit circumstance",
                  onClick: () => openRefillTooSoonDrawer({ cartId: cart.id, cartItem }),
                  hasRightIcon: true,
                },
              ]}
            >
              Special circumstance for early refill:{" "}
              {refillTooSoonOptions[cartItem.refill_too_soon_reason]}
            </Alert>
          )}
        </div>
      </StepLayout.Item.Info>
      <StepLayout.Item.Content>
        {steps.map(({ title, content }, index) => (
          <StepLayout.Section key={title}>
            <StepLayout.Header>
              <StepLayout.Heading>
                {steps.length > 1 && `${index + 1}. `}
                {title}
              </StepLayout.Heading>
            </StepLayout.Header>
            {content}
          </StepLayout.Section>
        ))}
      </StepLayout.Item.Content>
      <AddPatientModal
        isVisible={isAddPatientModalVisible}
        onSuccess={selectPatient}
        onClose={() => setIsAddPatientModalVisible(false)}
      />
      <CartItemAssignmentModal
        cartItem={cartItem}
        cart={cart}
        isVisible={isSplitPatientModalVisible}
        onClose={() => setIsSplitPatientModalVisible(false)}
      />
    </StepLayout.Item>
  );
};

type ItemOptionsCheckoutStepProps = CheckoutStepProps;

const ItemOptionsCheckoutStep = (props: ItemOptionsCheckoutStepProps) => {
  const { cart, onPrevious, onNext, isValid, isLoading, refillTooSoonSummary } = props;
  // Warm the default first page so every cart-item picker shares one cached request.
  usePaginatedPatientsQuery({
    page: 1,
    page_size: PATIENT_LIST_PAGE_SIZE,
    q: "",
  });

  const isClinicCreatedCart = cart.creator_type === CartCreatorKind.Clinic;

  const cartItemIdToRefillTooSoonSummary: Partial<
    Record<number, CartItemRefillTooSoonSummary | undefined>
  > = indexByPrimaryKey(refillTooSoonSummary.cart_items, "id");

  const invalidateCartQueries = useInvalidateAllCartQueries();

  const onAccountUpdateSuccess = () => {
    invalidateCartQueries();
  };

  const { open: openAccountUpdate } = useAccountUpdateDrawer();

  return (
    <StepLayout>
      <StepLayout.Section>
        {!!cart.missing_required_client_fields_message && (
          <Alert
            variant="danger"
            shouldScrollOnMount={true}
            actions={[
              {
                label: "Update",
                /* TODO: [sc-41928] add new checkout profile update form here */
                onClick: () =>
                  openAccountUpdate({
                    updatePhoneNumber: true,
                    onSuccess: onAccountUpdateSuccess,
                  }),
                hasRightIcon: true,
              },
            ]}
          >
            {cart.missing_required_client_fields_message}
          </Alert>
        )}
        <StepLayout.Title>Cart Items</StepLayout.Title>
        <StepLayout.Section>
          {cart.items.map((cartItem, index) => (
            <CartItemSelections
              key={cartItem.id}
              cartItem={cartItem}
              refillTooSoonSummary={cartItemIdToRefillTooSoonSummary[cartItem.id]}
              cart={cart}
              heading={`Item ${index + 1} of ${cart.items.length}`}
            />
          ))}
        </StepLayout.Section>
      </StepLayout.Section>
      <div className={styles.priceSummaryContainer}>
        <StepLayout.Section>
          <div className={styles.priceSummary}>
            <StepLayout.Title>Order Summary</StepLayout.Title>
            <PromoCodes cart={cart} />
            <FreeShippingProgress
              cart={cart}
              surface={FreeShippingProgressSurface.ItemOptionsSummary}
            />
            <CheckoutSummary.PriceSummary cart={cart} />
          </div>
        </StepLayout.Section>
      </div>
      <StepLayout.Actions>
        <StepLayout.ActionButton.Previous onClick={onPrevious}>
          {/* no concept of "returning to cart" for client when it was a clinic-created cart */}
          {isClinicCreatedCart ? "Continue Shopping" : "Return to Cart"}
        </StepLayout.ActionButton.Previous>
        <StepLayout.ActionButton.Next
          isLoading={isLoading}
          onClick={onNext}
          disabled={!isValid}
          disabledHelp={
            cart.warning_messages.length
              ? "Please fix the issues with your cart items before proceeding."
              : "Please fill out all fields and fix any issues before proceeding."
          }
        >
          Continue to Shipping
        </StepLayout.ActionButton.Next>
      </StepLayout.Actions>
    </StepLayout>
  );
};

export default ItemOptionsCheckoutStep;
